import { matchesDepartmentGroup } from '../shared/departmentGroup/departmentGroup';
import { CollectionScheduledEmail, CollectionScheduleStatus } from './model';

export interface ScheduledEmailFilterFields {
  company: string;
  tone: '' | CollectionScheduledEmail['tone'];
  nextSendDate: string;
  status: '' | CollectionScheduleStatus;
  departmentGroup: string;
}

export const EMPTY_SCHEDULED_EMAIL_FILTERS: ScheduledEmailFilterFields = {
  company: '',
  tone: '',
  nextSendDate: '',
  status: '',
  departmentGroup: 'all',
};

export const SCHEDULE_STATUS_FILTER_LABELS: Record<CollectionScheduleStatus, string> = {
  ready: 'Ready',
  manual_locked: 'Manual locked',
  auto_skipped: 'Auto skipped',
  auto_paused: 'Automatic paused',
  no_recipients: 'No recipients',
};

const SCHEDULE_STATUSES: CollectionScheduleStatus[] = ['ready', 'auto_skipped', 'no_recipients'];

export const SCHEDULE_STATUS_FILTER_OPTIONS = SCHEDULE_STATUSES.map((status) => ({
  value: status,
  label: SCHEDULE_STATUS_FILTER_LABELS[status],
}));

/** Date portion of API next_send_at (mm/dd/yyyy from backend strftime). */
export const nextSendDatePart = (nextSendAt: string | null): string | null => {
  if (!nextSendAt) return null;
  const match = /^(\d{2}\/\d{2}\/\d{4})/.exec(nextSendAt.trim());
  return match ? match[1] : null;
};

/** Parse user input in mm/dd/yyyy (or m/d/yyyy) to zero-padded mm/dd/yyyy, or null if invalid. */
export const parseMmDdYyyy = (input: string): string | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const parsed = new Date(year, month - 1, day);
  if (parsed.getFullYear() !== year || parsed.getMonth() + 1 !== month || parsed.getDate() !== day) {
    return null;
  }
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${mm}/${dd}/${year}`;
};

export const matchesNextSendDateFilter = (nextSendAt: string | null, dateFilter: string): boolean => {
  const q = dateFilter.trim();
  if (!q) return true;
  const rowDate = nextSendDatePart(nextSendAt);
  if (!rowDate) return false;

  const normalized = parseMmDdYyyy(q);
  if (normalized) {
    return rowDate === normalized;
  }
  return rowDate.startsWith(q);
};

export const filterScheduledEmails = (
  rows: CollectionScheduledEmail[],
  filters: ScheduledEmailFilterFields,
  myGroups: string[],
): CollectionScheduledEmail[] => {
  const companyQuery = filters.company.trim().toLowerCase();

  return rows.filter((row) => {
    if (companyQuery && !row.company.toLowerCase().includes(companyQuery)) return false;
    if (filters.tone && row.tone !== filters.tone) return false;
    if (filters.status && row.schedule_status !== filters.status) return false;
    if (!matchesNextSendDateFilter(row.next_send_at, filters.nextSendDate)) return false;
    if (!matchesDepartmentGroup(row, filters.departmentGroup, myGroups)) return false;
    return true;
  });
};
